import { useMemo } from 'react';

import { parseDate } from '@internationalized/date';
import { AxiosError } from 'axios';
import useSWR from 'swr';

import {
  isDefined,
  NcEstimatedMinimumPaymentT,
  WithStatusT,
} from '@consumer-frontend/nc-utils';

import { apiEndpoints } from '../lib';

export type DrawPaymentT = {
  effective_date: string;
  finance_charge: string;
  minimum_payment_amount: string;
  outstanding_balance: string;
  principal_due: string;
};

export type UseDrawPaymentSchedulePropsT = {
  requestedAmount?: number;
};

export type UseDrawPaymentScheduleResponseT = {
  draw_payment_schedule: DrawPaymentT[];
};

export type UseDrawPaymentScheduleResultT = {
  isLoading: boolean;
  error?: AxiosError;
  minimumPayments?: NcEstimatedMinimumPaymentT[];
  status?: number;
};

export const useDrawPaymentSchedule = ({
  requestedAmount,
}: UseDrawPaymentSchedulePropsT = {}): UseDrawPaymentScheduleResultT => {
  // eslint-disable-next-line @stylistic/object-curly-newline
  const { data, error, isLoading } = useSWR<WithStatusT<UseDrawPaymentScheduleResponseT>, AxiosError>(
    () => (isDefined(requestedAmount) && requestedAmount > 0
      ? apiEndpoints.dashboard.drawPaymentSchedule({ requested_amount: requestedAmount.toString() })
      : null
    ),
  );

  const minimumPayments = useMemo(
    () => (
      data?.draw_payment_schedule.map((payment) => ({
        feeDue: payment.finance_charge,
        minimumPayment: payment.minimum_payment_amount,
        outstandingPrincipal: payment.outstanding_balance,
        paymentDate: parseDate(payment.effective_date),
        principalDue: payment.principal_due,
      }))
    ),
    [data],
  );

  return {
    error,
    isLoading,
    minimumPayments,
    status: data?.status || error?.status,
  };
};
